import React, { useState, useEffect, useRef } from 'react';
import "../styles/Contact.css";

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const contactRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (contactRef.current) {
      observer.observe(contactRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    alert('Thank you for your message! We\'ll get back to you soon.');
    setFormData({ name: '', email: '', message: '' });
  };

  const contactInfo = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
          <path d="M224.2 89C216.3 70.1 195.7 60.1 176.1 65.4L170.6 66.9C106 84.5 50.8 147.1 66.9 223.3C104 398.3 241.7 536 416.7 573.1C493 589.3 555.5 534 573.1 469.4L574.6 463.9C580 444.2 569.9 423.6 551.1 415.8L453.8 375.3C437.3 368.4 418.2 373.2 406.8 387.1L368.2 434.3C297.9 399.4 241.3 341 208.8 269.3L253 233.3C266.9 222 271.6 202.9 264.8 186.3L224.2 89z" />
        </svg>
      ),
      title: "Phone Number",
      content: "+1 (987) 654-3210",
      description: "Available 24/7 for urgent inquiries"
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
          <path d="M112 128C85.5 128 64 149.5 64 176C64 191.1 71.1 205.3 83.2 214.4L291.2 370.4C308.3 383.2 331.7 383.2 348.8 370.4L556.8 214.4C568.9 205.3 576 191.1 576 176C576 149.5 554.5 128 528 128L112 128zM64 260L64 448C64 483.3 92.7 512 128 512L512 512C547.3 512 576 483.3 576 448L576 260L377.6 408.8C343.5 434.4 296.5 434.4 262.4 408.8L64 260z" />
        </svg>
      ),
      title: "Email Address",
      content: "hello@yourcompany.com",
      description: "We respond within 2 business hours"
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
          <path d="M128 252.6C128 148.4 214 64 320 64C426 64 512 148.4 512 252.6C512 371.9 391.8 514.9 341.6 569.4C329.8 582.2 310.1 582.2 298.3 569.4C248.1 514.9 127.9 371.9 127.9 252.6zM320 320C355.3 320 384 291.3 384 256C384 220.7 355.3 192 320 192C284.7 192 256 220.7 256 256C256 291.3 284.7 320 320 320z" />
        </svg>
      ),
      title: "Our Headquarters",
      content: "123 Innovation Street, Tech City",
      description: "Visit us for a consultation"
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
          <path d="M528 320C528 434.9 434.9 528 320 528C205.1 528 112 434.9 112 320C112 205.1 205.1 112 320 112C434.9 112 528 205.1 528 320zM64 320C64 461.4 178.6 576 320 576C461.4 576 576 461.4 576 320C576 178.6 461.4 64 320 64C178.6 64 64 178.6 64 320zM296 184L296 320C296 328 300 335.5 306.7 340L402.7 404C413.7 411.4 428.6 408.4 436 397.3C443.4 386.2 440.4 371.4 429.3 364L344 307.2L344 184C344 170.7 333.3 160 320 160C306.7 160 296 170.7 296 184z" />
        </svg>
      ),
      title: "Operational Hours",
      content: "Mon - Fri: 9AM - 6PM",
      description: "Weekend appointments available"
    }
  ];

  return (
    <div className="contact-container" ref={contactRef}>
      {/* Hero Section */}
      <section className={`contact-hero ${isVisible ? 'visible' : ''}`}>
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">
            Ready to Elevate <span className="highlight">Your Project?</span>
          </h1>
          <p className="hero-subtitle">
            Let's create something extraordinary together. Get in touch and let's start your journey.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="contact-main">
        <div className="contact-grid">
          {/* Contact Information */}
          <div className="contact-info-section">
            <h2 className="section-title">Get In Touch</h2>
            <p className="section-description">
              We're here to help you bring your ideas to life. Reach out through any channel that works for you.
            </p>
            
            <div className="contact-info-grid">
              {contactInfo.map((item, index) => (
                <div 
                  key={index} 
                  className={`contact-card fade-in-up ${isVisible ? 'visible' : ''}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="contact-icon">
                    {item.icon}
                  </div>
                  <div className="contact-details">
                    <h3>{item.title}</h3>
                    <p className="contact-content">{item.content}</p>
                    <p className="contact-description">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Social Links */}
            <div className="social-links">
              <h3>Follow Us</h3>
              <div className="social-icons">
                {['LinkedIn', 'Twitter', 'GitHub', 'Dribbble'].map((platform) => (
                  <a key={platform} href="#" className="social-icon" aria-label={platform}>
                    {platform}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="map-section">
          <h2 className="section-title">Find Us Here</h2>
          <div className="map-container">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1360.2384665214831!2d77.70444200502446!3d11.334721041944237!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba96f3ca9359155%3A0x1806bef2ddb05ce4!2sNew%20Teachers%20Colony%2C%20Sampath%20Nagar%2C%20Tamil%20Nadu%20638011!5e0!3m2!1sen!2sin!4v1760705197563!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0, borderRadius: "12px" }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Office Location"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;